/**
 * Find Work (Opportunities)
 * Public projects a contractor can send a quote on.
 */

import { Briefcase, CheckCircle2 } from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyInput } from "@/components/ui/currency-input";
import { QueryErrorState } from "@/components/ui/query-error-state";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
	expressInterest,
	fetchContractorProfile,
	fetchPublicProjects,
	type PublicProjectSummary,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	Chip,
	FloatPanel,
	PageHeader,
	Pill,
	StudioButton,
} from "@/features/studio/theme";
import { formatPrice } from "@/lib/format";
import { resolveMediaUrl } from "@/lib/utils";

type ScopeFilter = "" | "full_home" | "partial";
type SortOrder = "newest" | "budgetHighToLow" | "budgetLowToHigh";

const SCOPE_FILTERS: { value: ScopeFilter; labelKey: string }[] = [
	{ value: "", labelKey: "opportunities.allScopes" },
	{ value: "full_home", labelKey: "opportunities.fullHome" },
	{ value: "partial", labelKey: "opportunities.partial" },
];

const TEXTAREA_CLASS =
	"w-full rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3.5 py-2.5 font-body text-sm text-dashboard-text outline-none transition-shadow placeholder:text-dashboard-placeholder focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]";

function useBudgetLabel(): (project: PublicProjectSummary) => string | null {
	const { t } = useTranslation();
	return (project) => {
		const { budgetMin, budgetMax } = project;
		if (budgetMin != null && budgetMax != null) {
			return `${formatPrice(budgetMin)} – ${formatPrice(budgetMax)}`;
		}
		if (budgetMin != null) {
			return t("opportunities.budgetFrom", { amount: formatPrice(budgetMin) });
		}
		if (budgetMax != null) {
			return t("opportunities.budgetUpTo", { amount: formatPrice(budgetMax) });
		}
		return null;
	};
}

function ProjectCard({
	project,
	onQuote,
	canQuote,
}: {
	project: PublicProjectSummary;
	onQuote: (project: PublicProjectSummary) => void;
	canQuote: boolean;
}) {
	const { t } = useTranslation();
	const budgetLabel = useBudgetLabel();
	const budget = budgetLabel(project);
	const cover = project.previewImages?.[0];
	const rooms = project.scopeDetails?.rooms ?? [];
	const place = project.projectCity;

	return (
		<FloatPanel
			elevation={1}
			padding={0}
			className="flex flex-col overflow-hidden"
		>
			{cover ? (
				<img
					src={resolveMediaUrl(cover)}
					alt=""
					loading="lazy"
					className="aspect-[16/9] w-full object-cover"
				/>
			) : null}
			<div className="flex flex-1 flex-col p-5 sm:p-6">
				<div className="flex items-start justify-between gap-3">
					<h3 className="min-w-0 font-display text-lg font-medium leading-snug text-dashboard-text">
						{project.title}
					</h3>
					<Pill tone="neutral">
						{project.scope === "full_home"
							? t("opportunities.fullHome")
							: t("opportunities.partial")}
					</Pill>
				</div>
				<p className="mt-1 font-body text-[13px] text-dashboard-muted">
					{place
						? t("opportunities.postedByIn", {
								name: project.homeownerName,
								city: place,
							})
						: t("opportunities.postedBy", { name: project.homeownerName })}
				</p>

				{project.description ? (
					<p className="mt-3 line-clamp-2 font-body text-sm leading-relaxed text-dashboard-muted-dark">
						{project.description}
					</p>
				) : null}

				{(budget || project.timelineWeeks != null) && (
					<dl className="mt-4 grid grid-cols-2 gap-3 border-t border-dashboard-divider pt-4">
						{budget && (
							<div>
								<dt className="font-body text-xs text-dashboard-muted">
									{t("opportunities.budget")}
								</dt>
								<dd className="mt-0.5 font-body text-sm font-semibold text-dashboard-text">
									{budget}
								</dd>
							</div>
						)}
						{project.timelineWeeks != null && (
							<div>
								<dt className="font-body text-xs text-dashboard-muted">
									{t("opportunities.timeline")}
								</dt>
								<dd className="mt-0.5 font-body text-sm font-semibold text-dashboard-text">
									{t("opportunities.weeksTimeline", {
										count: project.timelineWeeks,
									})}
								</dd>
							</div>
						)}
					</dl>
				)}

				{rooms.length > 0 && (
					<ul
						className="mt-4 flex flex-wrap gap-1.5"
						aria-label={t("opportunities.rooms")}
					>
						{rooms.slice(0, 4).map((room) => (
							<li
								key={room}
								className="rounded-full bg-dashboard-warm-050 px-2.5 py-1 font-body text-xs text-dashboard-muted-dark"
							>
								{room}
							</li>
						))}
						{rooms.length > 4 && (
							<li className="rounded-full bg-dashboard-warm-050 px-2.5 py-1 font-body text-xs text-dashboard-muted-dark">
								+{rooms.length - 4}
							</li>
						)}
					</ul>
				)}

				<div className="mt-auto pt-5">
					<StudioButton
						variant="secondary"
						size="sm"
						onClick={() => onQuote(project)}
						disabled={!canQuote}
						style={{ width: "100%" }}
					>
						{t("opportunities.sendQuote")}
					</StudioButton>
				</div>
			</div>
		</FloatPanel>
	);
}

export function OpportunitiesPage() {
	const { t } = useTranslation();
	const budgetLabel = useBudgetLabel();
	const [projects, setProjects] = useState<PublicProjectSummary[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	// The backend refuses a quote from an unverified contractor. Until the
	// profile says otherwise -- or if it cannot be read -- quoting stays open
	// and the backend decides.
	const [isVerified, setIsVerified] = useState(true);
	useEffect(() => {
		fetchContractorProfile()
			.then((profile) => setIsVerified(profile?.isVerified ?? true))
			.catch(() => setIsVerified(true));
	}, []);

	const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("");
	const [sortBy, setSortBy] = useState<SortOrder>("newest");

	const [selectedProject, setSelectedProject] =
		useState<PublicProjectSummary | null>(null);
	const [quoteAmount, setQuoteAmount] = useState("");
	const [quoteDetails, setQuoteDetails] = useState("");
	const [message, setMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [submitSuccess, setSubmitSuccess] = useState(false);

	const fetchProjects = useCallback(async () => {
		setIsLoading(true);
		setLoadFailed(false);
		try {
			const result = await fetchPublicProjects({
				scope: scopeFilter || undefined,
				page,
				limit: 12,
			});
			setProjects(result.projects);
			setTotal(result.total);
			setTotalPages(result.totalPages);
		} catch {
			setLoadFailed(true);
		} finally {
			setIsLoading(false);
		}
	}, [scopeFilter, page]);

	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	// The API pages newest-first; the budget orders rearrange the page in view.
	const sortedProjects = [...projects].sort((a, b) => {
		if (sortBy === "budgetHighToLow") {
			return (b.budgetMax ?? 0) - (a.budgetMax ?? 0);
		}
		if (sortBy === "budgetLowToHigh") {
			return (a.budgetMin ?? 0) - (b.budgetMin ?? 0);
		}
		return 0;
	});

	const openQuote = (project: PublicProjectSummary) => {
		setSelectedProject(project);
		setQuoteAmount("");
		setQuoteDetails("");
		setMessage("");
		setSubmitError("");
		setSubmitSuccess(false);
	};

	const handleSubmitInterest = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedProject) return;

		const amount = parseInt(quoteAmount, 10);
		if (!quoteAmount || Number.isNaN(amount) || amount <= 0) {
			setSubmitError(t("opportunities.invalidQuoteAmount"));
			return;
		}

		setIsSubmitting(true);
		setSubmitError("");

		const result = await expressInterest({
			projectId: selectedProject.id,
			quoteAmount: amount,
			quoteDetails: quoteDetails.trim() || undefined,
			message: message.trim() || undefined,
		});

		setIsSubmitting(false);

		if (result.success) {
			setSubmitSuccess(true);
			// A project you have quoted on leaves the list.
			setProjects((prev) => prev.filter((p) => p.id !== selectedProject.id));
			setTotal((prev) => prev - 1);
		} else {
			setSubmitError(result.error || t("opportunities.quoteFailed"));
		}
	};

	const closeSheet = () => {
		setSelectedProject(null);
		setSubmitSuccess(false);
	};

	const selectedBudget = selectedProject ? budgetLabel(selectedProject) : null;

	return (
		<div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:py-10">
			<PageHeader
				title={t("opportunities.title")}
				description={t("opportunities.subtitle")}
			/>

			{isVerified ? null : (
				<FloatPanel elevation={1} padding={16}>
					<p
						role="note"
						className="font-body text-sm text-dashboard-muted-dark"
					>
						{t("opportunities.unverifiedNotice")}
					</p>
				</FloatPanel>
			)}

			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<fieldset className="flex flex-wrap gap-2">
					<legend className="sr-only">
						{t("opportunities.filterByScope")}
					</legend>
					{SCOPE_FILTERS.map((filter) => (
						<Chip
							key={filter.value || "all"}
							active={scopeFilter === filter.value}
							onClick={() => {
								setScopeFilter(filter.value);
								setPage(1);
							}}
						>
							{t(filter.labelKey)}
						</Chip>
					))}
				</fieldset>
				<label className="flex items-center gap-2 font-body text-sm text-dashboard-muted">
					{t("opportunities.sortBy")}
					<select
						value={sortBy}
						onChange={(e) => setSortBy(e.target.value as SortOrder)}
						className="h-9 rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3 font-body text-sm text-dashboard-text outline-none focus:border-dashboard-accent"
					>
						<option value="newest">{t("opportunities.newest")}</option>
						<option value="budgetHighToLow">
							{t("opportunities.budgetHighToLow")}
						</option>
						<option value="budgetLowToHigh">
							{t("opportunities.budgetLowToHigh")}
						</option>
					</select>
				</label>
			</div>

			<p className="font-body text-sm text-dashboard-muted" aria-live="polite">
				{isLoading
					? t("common.loading")
					: loadFailed
						? ""
						: t("opportunities.projectCount", { count: total })}
			</p>

			{loadFailed ? (
				<FloatPanel elevation={1} padding={32}>
					<QueryErrorState
						message={t("opportunities.loadError")}
						onRetry={fetchProjects}
					/>
				</FloatPanel>
			) : isLoading ? (
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
					{[1, 2, 3].map((i) => (
						<Skeleton key={i} className="h-72 rounded-2xl" />
					))}
				</div>
			) : sortedProjects.length === 0 ? (
				<FloatPanel elevation={1} padding={32}>
					<div className="mx-auto flex max-w-md flex-col items-center text-center">
						<Briefcase
							className="h-8 w-8 text-dashboard-muted-light"
							aria-hidden="true"
						/>
						<h2 className="mt-4 font-display text-xl font-medium text-dashboard-text">
							{t("opportunities.emptyTitle")}
						</h2>
						<p className="mt-2 font-body text-sm text-dashboard-muted">
							{scopeFilter
								? t("opportunities.emptyFiltered")
								: t("opportunities.emptyDescription")}
						</p>
						{scopeFilter ? (
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() => {
									setScopeFilter("");
									setPage(1);
								}}
								style={{ marginTop: 16 }}
							>
								{t("opportunities.showAll")}
							</StudioButton>
						) : null}
					</div>
				</FloatPanel>
			) : (
				<>
					<div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
						{sortedProjects.map((project) => (
							<ProjectCard
								key={project.id}
								project={project}
								onQuote={openQuote}
								canQuote={isVerified}
							/>
						))}
					</div>

					{totalPages > 1 && (
						<nav
							className="flex items-center justify-center gap-3"
							aria-label={t("opportunities.pagination")}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
							>
								{t("common.previous")}
							</StudioButton>
							<span className="font-body text-sm text-dashboard-muted">
								{t("common.pageOf", { page, totalPages })}
							</span>
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page === totalPages}
							>
								{t("common.next")}
							</StudioButton>
						</nav>
					)}
				</>
			)}

			<Sheet
				open={!!selectedProject}
				onOpenChange={(open) => !open && closeSheet()}
			>
				<SheetContent
					side="right"
					className="w-full overflow-y-auto bg-dashboard-bg sm:max-w-md"
				>
					{submitSuccess ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<CheckCircle2
								className="mb-4 h-14 w-14 text-dashboard-pill-done"
								aria-hidden="true"
							/>
							<h3 className="font-display text-xl font-medium text-dashboard-text">
								{t("opportunities.interestSubmitted")}
							</h3>
							<p className="mt-2 max-w-[36ch] font-body text-sm text-dashboard-muted">
								{t("opportunities.interestSubmittedDescription")}
							</p>
							<StudioButton
								variant="primary"
								onClick={closeSheet}
								style={{ marginTop: 24 }}
							>
								{t("common.continue")}
							</StudioButton>
						</div>
					) : (
						<>
							<SheetHeader>
								<SheetTitle className="font-display text-xl font-medium text-dashboard-text">
									{t("opportunities.expressInterestTitle")}
								</SheetTitle>
								<SheetDescription className="font-body text-dashboard-muted">
									{t("opportunities.expressInterestDescription")}
								</SheetDescription>
							</SheetHeader>

							{selectedProject && (
								<div className="mt-5 space-y-5">
									<div className="rounded-xl border border-dashboard-border bg-dashboard-warm-150 p-4">
										<p className="font-display text-base font-medium text-dashboard-text">
											{selectedProject.title}
										</p>
										<p className="mt-1 font-body text-[13px] text-dashboard-muted">
											{[
												selectedProject.scope === "full_home"
													? t("opportunities.fullHome")
													: t("opportunities.partial"),
												selectedBudget,
												selectedProject.timelineWeeks != null
													? t("opportunities.weeksTimeline", {
															count: selectedProject.timelineWeeks,
														})
													: null,
											]
												.filter(Boolean)
												.join(", ")}
										</p>
									</div>

									<form onSubmit={handleSubmitInterest} className="space-y-4">
										<CurrencyInput
											id="interest-quote-amount"
											label={t("opportunities.quoteAmountLabel")}
											value={quoteAmount}
											onChange={setQuoteAmount}
											error={submitError && !quoteAmount ? submitError : ""}
											placeholder={t("opportunities.enterAmountPlaceholder")}
										/>

										<div className="space-y-1.5">
											<label
												htmlFor="interest-quote-details"
												className="font-body text-sm font-medium text-dashboard-text"
											>
												{t("opportunities.detailsOptional")}
											</label>
											<textarea
												id="interest-quote-details"
												value={quoteDetails}
												onChange={(e) => setQuoteDetails(e.target.value)}
												placeholder={t("opportunities.detailsPlaceholder")}
												rows={3}
												className={TEXTAREA_CLASS}
											/>
										</div>

										<div className="space-y-1.5">
											<label
												htmlFor="interest-message"
												className="font-body text-sm font-medium text-dashboard-text"
											>
												{t("opportunities.messageLabel")}
											</label>
											<textarea
												id="interest-message"
												value={message}
												onChange={(e) => setMessage(e.target.value)}
												placeholder={t("opportunities.messagePlaceholder")}
												rows={3}
												className={TEXTAREA_CLASS}
											/>
										</div>

										{submitError && quoteAmount && (
											<p className="font-body text-sm text-destructive">
												{submitError}
											</p>
										)}

										<div className="flex gap-3 pt-1">
											<StudioButton
												type="submit"
												variant="primary"
												disabled={isSubmitting}
												style={{ flex: 1 }}
											>
												{isSubmitting
													? t("opportunities.sendingQuote")
													: t("opportunities.sendQuote")}
											</StudioButton>
											<StudioButton
												variant="secondary"
												onClick={closeSheet}
												disabled={isSubmitting}
											>
												{t("common.cancel")}
											</StudioButton>
										</div>
									</form>
								</div>
							)}
						</>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
